import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import { Alert, Typography } from '@mui/material';

import { Loader } from '@/components/feedback/Loader';
import type { Signature } from '@/features/annual-review';

import { SignatureTile } from './SignatureTile';

interface SignatureGalleryProps {
  signatures: Signature[];
  isLoading: boolean;
  isError: boolean;
  isDeleting: boolean;
  onView: (signature: Signature, label: string) => void;
  onRemove: (signature: Signature) => void;
}

/**
 * The four states of the signature list: loading, failed, empty, and populated.
 *
 * Split from `SignaturesCard` so the card is left holding dialogs and mutation
 * handling — keeping both put the card over the complexity limit, which is the
 * lint rule doing its job rather than something to raise.
 */
export const SignatureGallery = ({
  signatures,
  isLoading,
  isError,
  isDeleting,
  onView,
  onRemove,
}: SignatureGalleryProps) => {
  if (isLoading) return <Loader label="Loading your signatures" />;

  if (isError) {
    return (
      <Alert severity="error" role="alert" className="rounded-lg">
        Your signatures could not be loaded. Please refresh to try again.
      </Alert>
    );
  }

  if (signatures.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-divider px-4 py-8 text-center">
        <span className="flex size-11 items-center justify-center rounded-full bg-brand-100 text-primary-dark [&>svg]:text-[22px]">
          <DrawOutlinedIcon />
        </span>
        <Typography variant="subtitle2" component="p">
          No signatures saved yet
        </Typography>
        <Typography variant="body2" color="text.secondary" className="max-w-xs">
          Add one now, or one is saved automatically the first time you sign a document.
        </Typography>
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
      {signatures.map((signature, index) => (
        <SignatureTile
          key={signature.id}
          signature={signature}
          position={index + 1}
          onView={() => {
            onView(signature, `Signature ${String(index + 1)}`);
          }}
          onRemove={() => {
            onRemove(signature);
          }}
          isDeleting={isDeleting}
          isOnlySignature={signatures.length === 1}
        />
      ))}
    </ul>
  );
};
